import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, User, ArrowRight, Bot } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { apiErrorMessage } from '../lib/api';
import { toast } from 'sonner';
import Logo from '../components/Logo';

export default function LoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
  });
  const { login, register } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (isLogin) {
        const user = await login(formData.email, formData.password);
        toast.success(`Welcome back, ${user.name}!`);
        
        if (user.role === 'super_admin') {
          navigate('/admin');
        } else {
          navigate('/workspace');
        }
      } else {
        const user = await register({ email: formData.email, password: formData.password, name: formData.name });
        toast.success('Account created! Welcome aboard.');
        navigate('/workspace');
      }
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Authentication failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--background)', color: 'var(--foreground)' }}>
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="w-full max-w-md"
        >
          <Link to="/" className="flex items-center gap-2 mb-12">
            <Logo size={36} isDark={isDark} />
            <span className="font-heading font-semibold text-lg">XceedAgents</span>
            <span className="text-xs font-mono" style={{ color: 'var(--primary-accent)' }}>.AI</span>
          </Link>

          <h1 className="font-heading text-3xl font-semibold tracking-tight mb-2">
            {isLogin ? 'Welcome back' : 'Create your account'}
          </h1>
          <p className="text-sm mb-8" style={{ color: 'var(--text-muted)' }}>
            {isLogin 
              ? 'Sign in to access your workspace' 
              : 'Get started with a free proposal'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Full Name</label>
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                  <input
                    type="text"
                    required={!isLogin}
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Your name"
                    className="w-full rounded-xl pl-11 pr-4 py-3 text-sm outline-none transition-colors"
                    style={{ background: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--text-primary)' }}
                    data-testid="name-input"
                  />
                </div>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Email</label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="you@company.com"
                  className="w-full rounded-xl pl-11 pr-4 py-3 text-sm outline-none transition-colors"
                  style={{ background: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--text-primary)' }}
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Password</label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                <input
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="Min 8 characters"
                  className="w-full rounded-xl pl-11 pr-4 py-3 text-sm outline-none transition-colors"
                  style={{ background: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--text-primary)' }}
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button 
              type="submit"
              disabled={loading}
              className="w-full h-12 font-semibold rounded-xl gap-2 mt-2 hover-lift"
              style={{ background: 'var(--primary)', color: 'var(--background)' }}
              data-testid="auth-submit-btn"
            >
              {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm transition-colors"
              style={{ color: 'var(--text-muted)' }}
            >
              {isLogin 
                ? "Don't have an account? " 
                : 'Already have an account? '}
              <span className="font-medium" style={{ color: 'var(--primary-accent)' }}>
                {isLogin ? 'Sign up' : 'Sign in'}
              </span>
            </button>
          </div>

          <div className="mt-8 pt-6 border-t" style={{ borderColor: 'var(--border)' }}>
            <p className="text-xs text-center" style={{ color: 'var(--text-muted)' }}>
              By continuing, you agree to our Terms of Service and Privacy Policy
            </p>
          </div>
        </motion.div>
      </div>

      {/* Right Side - Visual */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden" style={{ background: 'var(--surface)' }}>
        <div className="relative z-10 flex flex-col items-center justify-center p-12 text-center w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-8" style={{ background: 'var(--tag-bg)' }}>
              <Bot className="w-8 h-8" style={{ color: 'var(--primary-accent)' }} />
            </div>
            <h2 className="font-heading text-3xl font-semibold tracking-tight mb-4">
              AI-Powered<br />Agentic Workflows
            </h2>
            <p className="max-w-sm mx-auto text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
              Access your workspace, view proposals, track projects, and collaborate with our AI agents.
            </p>
            
            <div className="mt-12 grid grid-cols-2 gap-4 max-w-sm mx-auto">
              {[
                { label: 'Projects Delivered', value: '500+' },
                { label: 'Client Satisfaction', value: '98%' },
                { label: 'Faster Delivery', value: '60%' },
                { label: 'Global Clients', value: '50+' },
              ].map((stat) => (
                <div key={stat.label} className="rounded-xl p-4 border" style={{ background: 'var(--background)', borderColor: 'var(--border)' }}>
                  <div className="text-xl font-semibold font-heading" style={{ color: 'var(--primary-accent)' }}>{stat.value}</div>
                  <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{stat.label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
